import { getErrorTypeFromUnknown } from "./ErrorParsers";

/**
 * Top-level framework error categories carried in `ErrorWithDebugInfos.type`.
 *
 * These are the broad transport-visible buckets. They are stable string
 * discriminators that travel across process and network boundaries.
 *
 * More specific framework failure reasons use `debugInfos.errorCode` and are
 * represented via `InternalErrors` when the type is `AtomErrors.InternalError`.
 */
export enum AtomErrors {
  CannotProvideAtom = "atom/cannot-provide",
  AtomIsNotRespawnable = "atom/not-respawnable",
  InternalError = "framework/internal",
  FailedToGetLock = "lock/failed-to-get",
  PersistenceServiceCriticalError = "persistence/critical",
  /**
   * A write hit a unique constraint that is not the id of an entity: the
   * value of a `@unique` field, or the key of a set, is taken. Only a code
   * (`debugInfos.errorCode`): the `type` of such an error stays
   * `PersistenceServiceCriticalError`.
   */
  UniqueConstraintViolation = "persistence/unique-violation",
  InsufficientPermission = "permission/insufficient",
  DeadLock = "lock/deadlock",
  LockTimedOut = "lock/timed-out",
}

/**
 * Prefix of the key under which an error with the code
 * `AtomErrors.UniqueConstraintViolation` names its constraint to the client:
 * `app.errors.unique.<constraint>`. A project gives the constraint a sentence
 * by adding that key to its i18n catalog.
 */
export const UNIQUE_VIOLATION_USER_MESSAGE_KEY_PREFIX = "app.errors.unique.";

const atomErrorTypes = new Set<string>(Object.values(AtomErrors));

export function getAtomErrorType(error: unknown): AtomErrors | undefined {
  const errorType = getErrorTypeFromUnknown(error);
  if (!errorType || !atomErrorTypes.has(errorType)) {
    return undefined;
  }

  return errorType as AtomErrors;
}
